/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback, useMemo } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import Autocomplete, {
  autocompleteClasses,
  AutocompleteOwnerState,
  AutocompleteProps,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import Popper, { PopperProps } from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * This is a wrapper around Material-ui Autocomplete component that uses react-window to render the options.
 * This is just to make it faster, by not rendering all the options at once, since there can be thousands of
 * options.  We don't want to put those all on the DOM.
 */
export function PopperAutocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined
>(props: AutocompleteProps<T, Multiple, DisableClearable, true /* freesolo */>) {
  const propsRenderOption = props.renderOption;

  const renderRow = useCallback(
    (rowProps: ListChildComponentProps): JSX.Element => {
      const { data, index, style } = rowProps;
      const dataSet = data[index];
      const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
      };

      return (
        <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
          {propsRenderOption ? (
            propsRenderOption(
              //@ts-ignore
              {},
              dataSet[1],
              {} as AutocompleteRenderOptionState,
              {} as AutocompleteOwnerState<T, Multiple, DisableClearable, true>
            )
          ) : (
            <>
              {dataSet[1]?.[1]}
              <span style={{ color: 'gray', marginLeft: 10 }}>[{dataSet[1]?.[0]}]</span>
            </>
          )}
        </Typography>
      );
    },
    [propsRenderOption]
  );

  // Adapter for react-window
  const ListboxComponent = useMemo(
    () =>
      React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
        const { children, ...other } = props;
        const itemData: React.ReactChild[] = [];
        (children as React.ReactChild[]).forEach((item: React.ReactChild & { children?: React.ReactChild[] }) => {
          itemData.push(item);
          itemData.push(...(item.children || []));
        });

        const theme = useTheme();
        const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
          noSsr: true,
        });
        const itemCount = itemData.length;
        const itemSize = smUp ? 36 : 48;

        const getChildSize = (child: React.ReactChild | undefined) => {
          if (child?.hasOwnProperty('group')) {
            return 48;
          }

          return itemSize;
        };

        const getHeight = () => {
          if (itemCount > 8) {
            return 8 * itemSize;
          }
          return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
        };

        const gridRef = useResetCache(itemCount);

        return (
          <div ref={ref}>
            <OuterElementContext.Provider value={other}>
              <VariableSizeList
                itemData={itemData}
                height={getHeight() + 2 * LISTBOX_PADDING}
                width="100%"
                ref={gridRef}
                outerElementType={OuterElementType}
                innerElementType="ul"
                itemSize={(index) => getChildSize(itemData[index])}
                overscanCount={5}
                itemCount={itemCount}
              >
                {renderRow}
              </VariableSizeList>
            </OuterElementContext.Provider>
          </div>
        );
      }),
    [renderRow]
  );

  return (
    <Autocomplete<T, Multiple, DisableClearable, true>
      {...props}
      freeSolo
      disableListWrap
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      renderOption={(props, option) => [props, option] as React.ReactNode}
    />
  );
}

const LISTBOX_PADDING = 8; // px

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    backgroundColor: 'var(--white)',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
}) as any as React.JSXElementConstructor<PopperProps>;
// John Tapsell: ^^ I had to cast to any as the typescript compiler was complaining about the PopperProps type not being compatible
// because Autcomplete.d.ts changed the type to Pick..  I'm not sure why this is happening, but I'm not sure it's worth
// spending time on.
